import { eraseCookie, setCookie } from "./cookies";

/**
 * A sign-in link for the team's test environments, so someone (or an assistant testing a change)
 * can switch accounts without typing a password:
 *
 *   https://dev.simbuy.io/dev-login#token=<jwt>&next=/orders
 *   https://dev.simbuy.io/dev-login#logout
 *
 * The token travels in the fragment, which the browser never sends to a server or puts in a log.
 * It is a normal access token, minted by whoever holds the environment's signing secret, so it
 * opens nothing a password would not; it lasts as long as its own expiry says. The link works only
 * on the hosts below — on any other, production included, it signs nobody in.
 */
export const DEV_LOGIN_PATH = "/dev-login";

const DEV_LOGIN_HOSTS = [
  "dev.simbuy.io",
  "qa.simbuy.io",
  "localhost",
  "127.0.0.1",
];

const COOKIE_DAYS = 1;

export type DevLogin =
  | { kind: "login"; token: string; userId: string; next: string }
  | { kind: "logout" };

/** An access token is three base64url parts; anything else could smuggle cookie attributes. */
const JWT_SHAPE = /^[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+$/;
const USER_ID_SHAPE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export const devLoginAllowed = (hostname: string) =>
  DEV_LOGIN_HOSTS.includes(hostname);

/** Where to go afterwards: a path inside the app, never another site. */
const safeNext = (next: string | null) =>
  next &&
  next.startsWith("/") &&
  !next.startsWith("//") &&
  !next.startsWith("/\\")
    ? next
    : "/";

const decodePayload = (
  token: string
): { sub?: string; exp?: number } | null => {
  const part = token.split(".")[1];
  if (!part) return null;
  try {
    const base64 = part.replace(/-/g, "+").replace(/_/g, "/");
    return JSON.parse(
      atob(base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), "="))
    );
  } catch {
    return null;
  }
};

/** What a dev-login link asks for, or null when it is not a usable one (no token, expired, malformed). */
export const parseDevLogin = (
  hash: string,
  now = Date.now()
): DevLogin | null => {
  const params = new URLSearchParams(hash.replace(/^#/, ""));
  if (params.has("logout")) return { kind: "logout" };
  const token = params.get("token");
  if (!token || !JWT_SHAPE.test(token)) return null;
  const payload = decodePayload(token);
  if (!payload?.sub || !USER_ID_SHAPE.test(payload.sub)) return null;
  if (payload.exp && payload.exp * 1000 <= now) return null;
  return {
    kind: "login",
    token,
    userId: payload.sub,
    next: safeNext(params.get("next")),
  };
};

const clearSession = () => {
  eraseCookie("accessToken");
  eraseCookie("userId");
  localStorage.removeItem("accessToken");
  localStorage.removeItem("userId");
  sessionStorage.removeItem("impersonationContext");
};

/**
 * Runs before the app reads its session: on a dev-login link, stores (or clears) the session and
 * moves to the page asked for, dropping the token from the address and the history. Anywhere else
 * it does nothing.
 *
 * @returns whether the address was a dev-login link
 */
export const applyDevLogin = (win: Window = window): boolean => {
  if (win.location.pathname !== DEV_LOGIN_PATH) return false;
  const login = devLoginAllowed(win.location.hostname)
    ? parseDevLogin(win.location.hash)
    : null;
  if (login?.kind === "logout") {
    clearSession();
  } else if (login?.kind === "login") {
    clearSession();
    setCookie("accessToken", login.token, COOKIE_DAYS);
    setCookie("userId", login.userId, COOKIE_DAYS);
    localStorage.setItem("accessToken", login.token);
    localStorage.setItem("userId", login.userId);
  }
  win.history.replaceState(
    null,
    "",
    login?.kind === "login" ? login.next : "/"
  );
  return true;
};
